import {useEffect, useRef} from 'react';
import {useSelector, useDispatch, useStore} from 'react-redux';
import {useParams, useLocation} from 'react-router-dom';
import type {Dispatch} from '@reduxjs/toolkit';
import type {Location} from 'react-router-dom';
import {selectConfig} from '@components/appSlice';
import {getTransactionIdFromSignPath} from '@utils/helpers';
import {selectAccessToken} from '@components/Login/userSlice';
import {parseJWT} from '@utils/jwtUtils';
import {AUTH_USER_ROLE} from '@constants/user';
import {saveUserRecipientIdToSession, getRecipientIdForSigning} from '@utils/recipientIdResolver';
import type {DocumentView} from '@api/types/document';
import type {ApprovalView} from '@api/approval/api.d';
import type {FieldView} from '@api/field/api.d';
import {loadSignerData, getMissingData} from '../dataLoader';
import {
    selectTransaction, selectActiveAccountId, selectVisibleDocuments, selectApprovals, selectFields,
    setUrlRecipientId, selectRecipient, resetSignerUiState
} from '../signerUiSlice';

interface RouteParams {
    packageId?: string;
    [key: string]: string | undefined;
}

interface JWTPayload {
    role?: string;
    external_id?: string;
}

interface UseSignerDataResult {
    transaction: any;
    activeAccountId: string | null;
    documents: DocumentView[];
    approvals: Record<string, ApprovalView[]>;
    fields: Record<string, FieldView[]>;
    hasValidTransaction: boolean;
    hasValidRecipient: boolean;
    hasValidUser: boolean;
    needsForceReload: boolean;
    urlTransactionId: string | null;
    urlRecipientId: string | null;
}

export function useSignerData(): UseSignerDataResult {
    const dispatch: Dispatch = useDispatch();
    const store = useStore();
    const params = useParams<RouteParams>();
    const location: Location = useLocation();

    const config = useSelector(selectConfig);
    const transaction = useSelector(selectTransaction);
    const activeAccountId = useSelector(selectActiveAccountId);
    const documents = useSelector(selectVisibleDocuments);
    const approvals = useSelector(selectApprovals);
    const fields = useSelector(selectFields);
    const accessToken = useSelector(selectAccessToken);
    const currentRecipient = useSelector(selectRecipient);

    const configRef = useRef(config);
    configRef.current = config;

    // Get transactionId from path and recipientId from query params
    const urlTransactionId: string | null = params.packageId || getTransactionIdFromSignPath(location.pathname);
    const searchParams = new URLSearchParams(location.search);
    const urlRecipientId: string | null = searchParams.get('recipientId');

    // Store recipientId in Redux for use in navigation
    useEffect(() => {
        dispatch(setUrlRecipientId(urlRecipientId));
    }, [urlRecipientId, dispatch]);

    // For role "user": save recipientId from URL to sessionStorage
    useEffect(() => {
        if (!accessToken || !urlRecipientId || !urlTransactionId) {
            return;
        }

        const tokenPayload = parseJWT(accessToken) as JWTPayload | null;
        const isUserRole = tokenPayload?.role === AUTH_USER_ROLE;

        if (isUserRole) {
            saveUserRecipientIdToSession(urlTransactionId, urlRecipientId);
        }
    }, [accessToken, urlRecipientId, urlTransactionId]);

    const recipientIdForSigning = getRecipientIdForSigning(accessToken, urlRecipientId, urlTransactionId);
    const tokenPayload = parseJWT(accessToken) as JWTPayload | null;
    const tokenExternalId = tokenPayload?.external_id;

    // Determine if user is Face-to-Face host
    const isUserRole = tokenPayload?.role === AUTH_USER_ROLE;
    const isFaceToFace = transaction?.settings?.face_to_face === true || transaction?.settings?.face_to_face === 'true';
    const isOwner = tokenExternalId === transaction?.owner_id;
    const isFaceToFaceHost = isFaceToFace && isUserRole && isOwner;

    // Check if we have valid data for current URL
    const hasValidTransaction = !!(transaction && transaction.id === urlTransactionId);
    const isRecipientMatchById = currentRecipient?.id === recipientIdForSigning;
    const isRecipientMatchByDeprecatedRoleId = currentRecipient?.deprecated_role_id === recipientIdForSigning;
    const isRecipientMatch = isRecipientMatchById || isRecipientMatchByDeprecatedRoleId;
    const hasValidRecipient = isFaceToFaceHost
        ? true
        : (!!currentRecipient && (!recipientIdForSigning || isRecipientMatch));
    const hasValidUser = !!activeAccountId;

    const isTransactionMismatch = urlTransactionId && transaction?.id !== urlTransactionId;
    const isRecipientStale = !!recipientIdForSigning && (!currentRecipient || !isRecipientMatch);
    const needsForceReload = !!(isTransactionMismatch || isRecipientStale);
    const loadingRef = useRef(false);

    useEffect(() => {
        if (!urlTransactionId) {
            return;
        }

        const state = store.getState() as any;
        const isAlreadyLoading = state.signerUi?.isInitialLoadInProgress || loadingRef.current;

        if (needsForceReload) {
            if (isAlreadyLoading) {
                return;
            }
            dispatch(resetSignerUiState());
            loadingRef.current = true;
            loadSignerData({
                dispatch,
                getState: store.getState,
                transactionId: urlTransactionId,
                config: configRef.current,
                accessToken,
                recipientId: recipientIdForSigning,
                forceReload: true
            }).finally(() => {
                loadingRef.current = false;
            });
        } else if (!isAlreadyLoading) {
            const missingData = getMissingData(state, urlTransactionId);
            const needsData = Object.keys(missingData).length > 0;

            if (needsData) {
                loadingRef.current = true;
                console.log('[useSignerData] calling loadSignerData (missing data)', Object.keys(missingData));
                loadSignerData({
                    dispatch,
                    getState: store.getState,
                    transactionId: urlTransactionId,
                    config: configRef.current,
                    accessToken,
                    recipientId: recipientIdForSigning,
                    forceReload: false
                })
                    .catch((error: Error) => {
                        console.error('[useSignerData] loadSignerData failed:', error);
                    })
                    .finally(() => {
                        loadingRef.current = false;
                    });
            }
        }
    }, [urlTransactionId, recipientIdForSigning, accessToken]);

    return {
        transaction,
        activeAccountId,
        documents,
        approvals,
        fields,
        hasValidTransaction,
        hasValidRecipient,
        hasValidUser,
        needsForceReload,
        urlTransactionId,
        urlRecipientId
    };
}
